import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { CREATE_APPLICATION } from "api/mutations/decisions";
import AlertError from "components/Alert/Error";
import Loading from "components/Loading";
import { applicationStepOne } from "pages/ApplicationForm/formSteps";
import { asMoney } from "utils/calcs";
import { renderDateTime } from "utils/helpers";

interface Decision {
  uuid: string;
  applicantNames: string;
  address: string;
  amount: number;
  termMonths: number;
  createdOn: string;
}

interface DecisionListProps {
  decisions?: Decision[];
}

const DecisionList: React.FC<DecisionListProps> = ({ decisions }) => {
  const [loadingDecisionToApp, setLoadingDecisionToApp] = useState(false);

  const navigate = useNavigate();

  const [
    createApplication,
    {
      data: dataCreateApplication,
      error: errorCreateApplication,
      reset: resetCreateApplication,
    },
  ] = useMutation(CREATE_APPLICATION);

  const handleContinueApplication = (decisionId: string) => {
    resetCreateApplication();
    setLoadingDecisionToApp(true);
    createApplication({ variables: { decisionId } });
  };

  useEffect(() => {
    if (dataCreateApplication?.decisions?.data?.uuid) {
      return navigate(
        applicationStepOne(dataCreateApplication.decisions.data.uuid),
      );
    }
  }, [dataCreateApplication, navigate, resetCreateApplication]);

  useEffect(() => {
    if (errorCreateApplication) {
      setLoadingDecisionToApp(false);
    }
  }, [errorCreateApplication]);

  return (
    <>
      {errorCreateApplication && (
        <div className="mb-4">
          <AlertError title="Error continuing to application">
            <p>
              Please try again, start a new application or contact us for
              support.
            </p>
          </AlertError>
        </div>
      )}

      <div className="overflow-hidden rounded-lg shadow ring-1 ring-black ring-opacity-5">
        <table className="w-full table-auto divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                Created
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                Applicant Name(s)
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                Address
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                Amount
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                Term Months
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              ></th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 bg-white">
            {loadingDecisionToApp ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center">
                  <Loading />
                  <p className="mt-4">
                    Loading & pre-populating application...
                  </p>
                </td>
              </tr>
            ) : (
              decisions &&
              decisions.map((d) => (
                <tr key={d.uuid}>
                  <td className="whitespace-nowrap px-3 py-3.5 text-sm text-gray-900">
                    {renderDateTime(d.createdOn)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3.5 text-sm">
                    {d.applicantNames}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3.5 text-sm">
                    {d.address}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3.5 text-sm">
                    £{asMoney(d.amount)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3.5 text-sm">
                    {d.termMonths}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3.5 text-right text-sm">
                    {loadingDecisionToApp ? (
                      <Loading small />
                    ) : (
                      <button
                        onClick={() => handleContinueApplication(d.uuid)}
                        className="link font-medium underline"
                      >
                        Start New Application
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default DecisionList;
